import { useState } from 'react';
import { StickyNote, Plus, Trash2, Edit2, X, Save } from 'lucide-react';
import { useNotes } from '../hooks/useNotes';

export function Notes() {
  const { notes, addNote, updateNote, deleteNote, getGeneralNotes } = useNotes();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  const generalNotes = getGeneralNotes();

  const handleSave = () => {
    if (title.trim() && content.trim()) {
      if (editingId) {
        updateNote(editingId, { title, content });
        setEditingId(null);
      } else {
        addNote({ title, content });
      }
      setTitle('');
      setContent('');
      setShowForm(false);
    }
  };

  const handleEdit = (note: { id: string; title: string; content: string }) => {
    setEditingId(note.id);
    setTitle(note.title);
    setContent(note.content);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setTitle('');
    setContent('');
  };

  const allNotes = [...generalNotes].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">My Notes</h1>
          <p className="text-dtf-text-muted">
            Keep track of observations, fixes tried, and shop learnings.
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-dtf-accent text-dtf-dark rounded-lg font-medium hover:bg-dtf-accent-hover transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Note
          </button>
        )}
      </div>

      {/* Note Form */}
      {showForm && (
        <div className="bg-dtf-panel border border-dtf-border rounded-xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Edit Note' : 'New Note'}
          </h3>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Note title..."
            className="w-full px-4 py-2 bg-dtf-dark border border-dtf-border rounded-lg text-white placeholder-dtf-gray mb-3 focus:outline-none focus:border-dtf-accent"
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your observations, fixes, or learnings..."
            rows={6}
            className="w-full px-4 py-2 bg-dtf-dark border border-dtf-border rounded-lg text-white placeholder-dtf-gray mb-4 focus:outline-none focus:border-dtf-accent resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-dtf-accent text-dtf-dark rounded-lg font-medium hover:bg-dtf-accent-hover transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Note
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center gap-2 px-4 py-2 text-dtf-text-muted hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      {allNotes.length === 0 ? (
        <div className="text-center py-16 bg-dtf-panel border border-dtf-border rounded-xl">
          <StickyNote className="w-16 h-16 text-dtf-gray mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Notes Yet</h3>
          <p className="text-dtf-text-muted mb-6">
            Start taking notes about your DTF printing experience.
          </p>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-2 bg-dtf-accent text-dtf-dark rounded-lg font-medium hover:bg-dtf-accent-hover transition-colors"
            >
              Create First Note
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {allNotes.map((note) => (
            <div
              key={note.id}
              className="bg-dtf-panel border border-dtf-border rounded-xl p-6 hover:border-dtf-accent/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">{note.title}</h3>
                  <p className="text-dtf-text whitespace-pre-wrap">{note.content}</p>
                  <p className="text-dtf-gray text-sm mt-4">
                    {new Date(note.createdAt).toLocaleDateString()} 
                    {note.updatedAt !== note.createdAt && ' (edited)'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(note)}
                    className="p-2 text-dtf-text-muted hover:text-white transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteNote(note.id)}
                    className="p-2 text-dtf-text-muted hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
